import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, Sparkles, MessageCircleQuestion, User } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import CosmicBackground from "../components/astro/CosmicBackground";
import api from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const SUGGESTED = [
  "When should I focus on my career?",
  "What are my strengths according to my Kundali?",
  "What should I know about my current Dasha?",
  "What areas of life should I focus on?",
  "What does my Moon Nakshatra represent?",
  "How is this year for marriage, traditionally?",
];

export default function AstroAsk() {
  const { lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`ask-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const endRef = useRef(null);
  const abortRef = useRef(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (question) => {
    const q = (question || input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      const r = await api.post("/astro/ask", { question: q, session_id: sessionId.current, lang },
                               { signal: abortRef.current.signal });
      setMessages((m) => [...m, { role: "ai", ...r.data }]);
    } catch (e) {
      if (e?.code === "ERR_CANCELED") return;
      toast.error(e?.response?.data?.detail || "The astrology AI is unavailable right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" data-testid="astro-ask-page">
      <Seo
        title="Ask the Astrology AI"
        description="Ask about career, marriage, love, education, finance, family or spiritual life and receive clear, respectful Vedic astrology guidance explained in simple language."
        path="/astrology/ask"
        keywords={["ask an astrologer", "astrology AI chat", "Vedic astrology questions"]}
      />
      <CosmicBackground />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
        <div className="mb-8 text-center">
          <span className="badge-gold mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-serif uppercase tracking-[0.28em]">
            <MessageCircleQuestion className="h-3.5 w-3.5" /> Ask the Astrology AI
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-3">
            <span className="text-gradient-flame">Ask, and understand</span>
          </h1>
          <p className="text-sm text-tara-body max-w-xl mx-auto">
            Traditional Vedic interpretation in plain language — never fear-based, never a guaranteed prediction.
          </p>
        </div>

        <div className="glass-liquid rounded-[1.8rem] p-5 sm:p-7" data-testid="ask-chat">
          <div className="max-h-[52vh] overflow-y-auto pr-1 space-y-4">
            {messages.length === 0 && (
              <div className="py-8 text-center">
                <Sparkles className="mx-auto mb-3 h-7 w-7 text-tara-teal/60" />
                <p className="text-sm text-tara-muted">Choose a question below, or write your own.</p>
              </div>
            )}

            {messages.map((m, i) => m.role === "user" ? (
              <div key={i} className="flex justify-end" data-testid={`chat-user-${i}`}>
                <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-gradient-to-br from-tara-tealDeep to-tara-teal px-4 py-3 text-sm text-white">
                  {m.text}
                </p>
              </div>
            ) : (
              <div key={i} className="flex gap-3" data-testid={`chat-ai-${i}`}>
                <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-tara-sindoor to-tara-vermilion">
                  <Sparkles className="h-4 w-4 text-white" />
                </span>
                <div className="glass max-w-[88%] rounded-2xl rounded-tl-sm p-4">
                  {m.answer.split("\n").filter(Boolean).map((para, pi) => (
                    <p key={pi} className="mb-2.5 text-sm leading-relaxed text-tara-body last:mb-0">{para}</p>
                  ))}
                  {m.key_points?.length > 0 && (
                    <ul className="mt-3 space-y-1.5 border-t border-tara-teal/15 pt-3">
                      {m.key_points.map((k, ki) => (
                        <li key={ki} className="flex gap-2 text-sm text-tara-ink"><span className="text-tara-teal">◆</span>{k}</li>
                      ))}
                    </ul>
                  )}
                  {m.follow_ups?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {m.follow_ups.map((f, fi) => (
                        <button key={fi} onClick={() => ask(f)}
                                className="rounded-full border border-tara-teal/30 bg-white/70 px-3.5 py-1.5 text-[11px] text-tara-tealDeep hover:border-tara-teal/60 transition-colors"
                                data-testid={`chat-followup-${i}-${fi}`}>
                          {f}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-3 text-sm text-tara-muted" data-testid="chat-loading">
                <Loader2 className="h-4 w-4 animate-spin text-tara-teal" /> Consulting the chart…
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-5 flex gap-2.5">
            <input
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about career, marriage, money, study, family or sadhana…"
              className="flex-1 rounded-full border border-tara-teal/25 bg-white/85 px-5 py-3.5 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-teal/60"
              data-testid="ask-input"
            />
            <button type="submit" disabled={loading} className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold" data-testid="ask-send-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>

        <div className="mt-7 flex flex-wrap gap-2.5" data-testid="ask-suggestions">
          {SUGGESTED.map((s, i) => (
            <button key={s} onClick={() => ask(s)}
                    className="glass rounded-full px-4 py-2.5 text-xs text-tara-ink hover:-translate-y-0.5 transition-transform"
                    data-testid={`ask-suggestion-${i}`}>
              <User className="mr-1.5 inline h-3 w-3 text-tara-teal" />{s}
            </button>
          ))}
        </div>

        <p className="mt-8 text-[11px] leading-relaxed text-tara-muted">
          Answers are traditional interpretations for reflection, not certainties, and never a substitute for
          medical, legal, mental-health or financial advice.
        </p>
      </section>
    </div>
  );
}
